import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'
import { Filesystem } from '@capacitor/filesystem'
import { setPendingSharedAudio } from '../../lib/sharedAudio'

const AUDIO_EXT_MIME: Record<string, string> = {
  m4a: 'audio/mp4',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  aac: 'audio/aac',
  aiff: 'audio/aiff',
  aif: 'audio/aiff',
  caf: 'audio/x-caf',
  ogg: 'audio/ogg',
  flac: 'audio/flac',
  mp4: 'audio/mp4'
}

/**
 * ネイティブ（iOS）で他アプリから共有された音声ファイルを受け取るハンドラ。
 * Info.plist の CFBundleDocumentTypes 宣言により、共有シートの
 * 「BluePaperで開く」からアプリの Inbox にコピーされ、file:// URL で通知される。
 * 受け取ったファイルは新規議事録作成ページ（音声アップロード）へ引き渡す。
 */
export function SharedFileReceiver() {
  const navigate = useNavigate()
  const handledUrls = useRef(new Set<string>())

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    const handleFileUrl = async (url: string) => {
      if (!url.startsWith('file://') || handledUrls.current.has(url)) return
      const fileName = decodeURIComponent(url.split('/').pop() || 'shared-audio')
      const ext = (fileName.split('.').pop() || '').toLowerCase()
      const mime = AUDIO_EXT_MIME[ext]
      if (!mime) return // 音声以外は無視
      handledUrls.current.add(url)

      try {
        const result = await Filesystem.readFile({ path: url })
        const base64 = typeof result.data === 'string' ? result.data : ''
        if (!base64) throw new Error('empty file')
        const blob = await (await fetch(`data:${mime};base64,${base64}`)).blob()
        const file = new File([blob], fileName, { type: mime })
        setPendingSharedAudio(file)
        navigate('/create?method=upload&shared=1')
      } catch (e) {
        console.error('共有ファイルの読み込みに失敗しました:', e)
      }
    }

    // コールドスタート時（共有からアプリが起動された場合）
    CapApp.getLaunchUrl().then((launch) => {
      if (launch?.url) handleFileUrl(launch.url)
    })

    // 起動中に共有された場合
    const listener = CapApp.addListener('appUrlOpen', (event) => {
      if (event.url) handleFileUrl(event.url)
    })

    return () => {
      listener.then((l) => l.remove())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}
